import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import {
  createDepartment,
  listEmployees,
  updateDepartment,
  type DepartmentRead,
} from '@/features/employees/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const schema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(100),
  description: z.string().trim().max(2000),
  head_id: z.string(),
})

type FormValues = z.infer<typeof schema>

/** Nested in the Employees screen rather than a screen of its own -- a handful of
 *  departments does not earn its own route, and the head picker is the employee list this
 *  screen already has to load. */
export function DepartmentFormDialog({
  open,
  department,
  onClose,
}: {
  open: boolean
  department?: DepartmentRead
  onClose: () => void
}) {
  const isEdit = department !== undefined
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  const employees = useQuery({
    queryKey: ['employees', 'department-heads'],
    queryFn: () => listEmployees({ status: 'active' }),
    retry: false,
    enabled: open,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: department?.name ?? '',
      description: department?.description ?? '',
      head_id: department?.head_id ?? '',
    },
  })

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const data = {
        name: values.name,
        description: values.description || null,
        head_id: values.head_id || null,
      }
      return department ? updateDepartment(department.id, data) : createDepartment(data)
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['departments'] })
      toast.success(isEdit ? `Saved ${saved.name}.` : `Created ${saved.name}.`)
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? `Edit ${department.name}` : 'New department'}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="department-form" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Create department'}
            </Button>
          </>
        }
      >
        <form id="department-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <Field label="Name" error={errors.name?.message}>
            {(props) => <Input {...props} {...register('name')} autoComplete="off" />}
          </Field>

          <Field label="Description" error={errors.description?.message} hint="Optional.">
            {(props) => <Input {...props} {...register('description')} autoComplete="off" />}
          </Field>

          <Field label="Head" error={errors.head_id?.message} hint="Optional.">
            {(props) => (
              <select {...props} {...register('head_id')} className={SELECT_CLASS} disabled={employees.isLoading}>
                <option value="">No head assigned</option>
                {(employees.data?.data ?? []).map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.full_name ?? employee.employee_code}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
